// A new session's time is a date, a start time and a length in minutes. The start is read in
// the browser's time zone and saved as a full timestamp, so the database (in UTC) keeps the
// moment the tutor meant, not the same clock time in UTC.

export const DEFAULT_SESSION_MINUTES = 60
export const MAX_SESSION_MINUTES = 120

export type SessionTimeInput = { date: string; startTime: string; minutes: string }

// The local start from a date input (yyyy-mm-dd) and a time input (hh:mm), or null if invalid.
export function sessionStart(date: string, startTime: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const t = /^(\d{2}):(\d{2})/.exec(startTime)
  if (!d || !t) return null
  const [year, month, day] = [Number(d[1]), Number(d[2]) - 1, Number(d[3])]
  const [hour, minute] = [Number(t[1]), Number(t[2])]
  const start = new Date(year, month, day, hour, minute)
  // new Date rolls Feb 30 over to March, 10:75 over to 11:15, and a time the clocks skip in
  // spring an hour on: refuse those instead.
  const same = start.getFullYear() === year && start.getMonth() === month && start.getDate() === day
    && start.getHours() === hour && start.getMinutes() === minute
  return same ? start : null
}

export function sessionTimeError({ date, startTime, minutes }: SessionTimeInput): string | null {
  if (!date) return 'Please enter the session date.'
  if (!startTime) return 'Please enter a start time.'
  if (!sessionStart(date, startTime)) return 'The date or start time is not valid.'
  if (!minutes.trim()) return 'Please enter how long the session was, in minutes.'
  const n = Number(minutes)
  if (!Number.isInteger(n) || n < 1) return 'The duration must be a whole number of minutes.'
  if (n > MAX_SESSION_MINUTES) return `A session can be at most ${MAX_SESSION_MINUTES} minutes.`
  return null
}

// Start, end and length of a valid session time; null while the form has an error.
export function sessionTimes(input: SessionTimeInput): { startedAt: Date; endedAt: Date; minutes: number } | null {
  if (sessionTimeError(input)) return null
  const startedAt = sessionStart(input.date, input.startTime) as Date
  const minutes = Number(input.minutes)
  return { startedAt, endedAt: new Date(startedAt.getTime() + minutes * 60000), minutes }
}
